import { type SearchPageState, type SelectionAssistantType } from "./types";
import {
  APPLY_ALL,
  type applyAllFiltersAction,
  CHANGE_CONTENT_PAGE,
  CHANGE_PAGE,
  CHANGE_VIEW_MODE,
  CLEAR_ALL,
  CLEAR_TECH_VALUES,
  CONTENT_SEARCH_SUCCESS,
  type FacetToggleData,
  REMOVE_BREADCRUMB,
  SEARCH_FAILURE,
  SEARCH_START,
  SEARCH_SUCCESS,
  SELECT_PRODUCT_GROUP,
  SELECT_PRODUCT_GROUP_MULTI,
  SET_SELECTED_PRODUCT_GROUPS,
  SRP_FETCH_RECOMMENDED_PRODUCTS_FAILURE,
  SRP_FETCH_RECOMMENDED_PRODUCTS_START,
  SRP_FETCH_RECOMMENDED_PRODUCTS_SUCCESS,
  TOGGLE_FACET,
  UPDATE_ASSISTANT_VALUES,
  UPDATE_CAMPAIGN_FILTER,
  UPDATE_IS_SELECTION_ASSISTANT,
  UPDATE_RELEVANT_FILTER,
  UPDATE_SEARCH_PARAMS_FROM_URL,
  UPDATE_SEARCH_TEXT,
  UPDATE_TECH_VALUE,
} from "./actions";
import {
  type BreadcrumbData,
  BreadcrumbType,
  type ContentDataItem,
  type FacetBreadcrumbResponse,
  type FacetData,
  type PaginationData,
  type SearchResultData,
  type SrpProductData,
  type TechValueBreadcrumb,
} from "../../utils/models";
import { type FromUrlSearchParams } from "../utils/historyManipulation";
import { type ActionType } from "../../redux/types";
import { SearchDisplayMode } from "../models";
import { diff } from "deep-diff";
import { sortInitialState, sortReducer } from "./reducer/sortReducer";
import { getInitialResultsViewMode } from "../utils/searchUtils";
import { RESET_SA_FILTERS } from "../../BearingSelectionAssistant/redux/actions";
import {
  SELECT_SERIES_TREE_NODE,
  SERIES_TREE_BACK,
  seriesTreeInitialState,
  seriesTreeReducer,
} from "./reducer/seriesTreeReducer";
import { UnitMode } from "../../ImperialMetricData/redux/UnitMode";
import { SearchPageType } from "~/Search/useSearchPageType";
import { produce } from "immer";
import { isTechValueFilterCategory } from "~/Search/filterConfigModels";
import type { GetFilterConfigApiResult } from "~/Search/api/fetchFilterConfig";

export const initialState: SearchPageState = {
  init: false,
  inFlight: false,

  text: "",
  currentPage: 0,

  pagination: {
    pageSize: 0,
    numberOfPages: 0,
    totalNumberOfResults: 0,
  },
  results: [],
  firstResultProductCode: null,

  contentResults: [],
  contentPagination: {
    pageSize: 0,
    numberOfPages: 0,
    totalNumberOfResults: 0,
  },
  contentPage: 0,

  facetValues: {},
  assistantValues: [],
  breadcrumbs: [],

  filterConfig: [],
  tabularViewColConfig: [],
  filterConfigImperial: [],
  tabularViewColConfigImperial: [],
  appliedTechFilterValues: {},
  rawTechFilterValues: {},

  sort: sortInitialState,

  fullTaxonomy: [],
  selectedProductGroups: [],

  seriesTree: seriesTreeInitialState,
  resultsViewMode: getInitialResultsViewMode(),

  hasTubularResults: false,

  technicalInfo: null,

  salesCampaignFilter: [],
  showOnlyRelevant: false,
  hasCampaignResults: false,

  productsOnSale: [],
  productsOnSaleInFlight: false,
};

export const srpReducer = (state = initialState, action: ActionType<object>): SearchPageState => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const nextSortState = sortReducer(state.sort, action as any);
  if (nextSortState !== state.sort) {
    state = {
      ...state,
      sort: nextSortState,
    };
  }

  const nextSeriesTreeState = seriesTreeReducer(state.seriesTree, action);
  if (nextSeriesTreeState !== state.seriesTree) {
    state = {
      ...state,
      seriesTree: nextSeriesTreeState,
    };
  }

  switch (action.type) {
    case UPDATE_SEARCH_PARAMS_FROM_URL: {
      const params = action.payload as FromUrlSearchParams;
      let rawTechFilterValues = state.rawTechFilterValues;
      let appliedTechFilterValues = state.appliedTechFilterValues;
      const techBreadcrumbs = params.breadcrumbs.filter(
        (bc) => bc.type === BreadcrumbType.TECH_VALUE,
      ) as TechValueBreadcrumb[];
      if (techBreadcrumbs.length > 0) {
        rawTechFilterValues = { ...rawTechFilterValues };
        appliedTechFilterValues = { ...appliedTechFilterValues };
        techBreadcrumbs.forEach((bc) => {
          const code = bc.code + "." + bc.valueType.toLowerCase();
          rawTechFilterValues[code] = bc.value;
          appliedTechFilterValues[code] = bc.value;
        });
      }

      return {
        ...state,
        inFlight: true,
        text: params.text || "",
        currentPage: params.currentPage ? +params.currentPage : 0,
        contentPage: 0,
        breadcrumbs: params.breadcrumbs,
        rawTechFilterValues,
        appliedTechFilterValues,
        assistantValues: params.assistantValues,
        selectedProductGroups: params.productGroups || [],
      };
    }

    case SEARCH_START: {
      return {
        ...state,
        inFlight: true,
      };
    }

    case SEARCH_FAILURE: {
      return {
        ...state,
        inFlight: false,
      };
    }

    case CHANGE_VIEW_MODE: {
      const mode = action.payload as unknown as SearchDisplayMode;
      return {
        ...state,
        resultsViewMode: mode,
      };
    }

    case SELECT_SERIES_TREE_NODE: {
      return {
        ...state,
        resultsViewMode: SearchDisplayMode.TABULAR,
      };
    }

    case SERIES_TREE_BACK: {
      return {
        ...state,
        resultsViewMode: SearchDisplayMode.SERIES_TREE,
      };
    }

    case SEARCH_SUCCESS: {
      const { data, selectedCategories, ignoreContent } = action.payload as {
        data: SearchResultData;
        selectedCategories: string[];
        ignoreContent?: boolean;
      };

      let firstResultProductCode = state.firstResultProductCode;
      if (state.currentPage === 0) {
        firstResultProductCode = data.results[0]?.productCode ?? null;
      }

      const breadcrumbs = state.breadcrumbs.map((item) => {
        if (item.type === BreadcrumbType.FACET) {
          const match = data.breadcrumbs.find(
            (facet): facet is FacetBreadcrumbResponse =>
              "facetValueCode" in facet && facet.facetValueCode === item.facetValueCode,
          );
          if (match) {
            item.name = match.facetValueName;
          }
        }
        return item;
      });

      return {
        ...state,
        init: true,
        inFlight: false,
        pagination: data.pagination,
        firstResultProductCode,
        results: data.results,
        facetValues: data.facets.reduce(
          (map, facet) => {
            map[facet.code] = facet;
            return map;
          },
          {} as Record<string, FacetData>,
        ),
        selectedProductGroups: selectedCategories,
        contentResults: ignoreContent ? state.contentResults : data.content.results,
        contentPagination: ignoreContent ? state.contentPagination : data.content.pagination,
        seriesTree: {
          ...state.seriesTree,
          tree: data.seriesTree ?? [],
        },
        hasTubularResults: data.hasTubularResults,
        technicalInfo: data.technicalInfo,
        hasCampaignResults: data.hasCampaignResults,

        filterConfig: data.filterConfig,
        filterConfigImperial: data.filterConfigImperial,
        tabularViewColConfig: data.tabularViewColConfig,
        tabularViewColConfigImperial: data.tabularViewColConfigImperial,

        fullTaxonomy: data.fullTaxonomy,
        breadcrumbs: breadcrumbs,
      };
    }

    case CONTENT_SEARCH_SUCCESS: {
      const { results, pagination } = action.payload as {
        results: ContentDataItem[];
        pagination: PaginationData;
      };

      return {
        ...state,
        inFlight: false,
        contentResults: results,
        contentPagination: pagination,
      };
    }

    case CHANGE_PAGE: {
      const pageNumber = action.payload as unknown as number;
      return {
        ...state,
        currentPage: pageNumber,
      };
    }

    case CHANGE_CONTENT_PAGE: {
      const pageNumber = action.payload as unknown as number;
      return {
        ...state,
        contentPage: pageNumber,
      };
    }

    case TOGGLE_FACET: {
      const data = action.payload as FacetToggleData;
      const newState = data.newValue;

      const newBreadcrumbs = newState
        ? state.breadcrumbs.concat({
            type: BreadcrumbType.FACET,
            facetValueCode: data.facetValueCode,
            facetCode: data.facetCode,
            name: data.facetName,
          })
        : state.breadcrumbs.filter(
            (bc) => bc.type !== BreadcrumbType.FACET || bc.facetValueCode !== data.facetValueCode,
          );

      return {
        ...state,
        currentPage: 0,
        breadcrumbs: newBreadcrumbs,
      };
    }

    case CLEAR_TECH_VALUES: {
      const techFilterUids = action.payload as string[];

      const rawTechFilterValues = { ...state.rawTechFilterValues };
      const appliedTechFilterValues = { ...state.appliedTechFilterValues };

      techFilterUids.forEach((code) => {
        delete rawTechFilterValues[code + ".min"];
        delete rawTechFilterValues[code + ".max"];
        delete appliedTechFilterValues[code + ".min"];
        delete appliedTechFilterValues[code + ".max"];
      });

      return {
        ...state,
        rawTechFilterValues,
        appliedTechFilterValues,
      };
    }

    case REMOVE_BREADCRUMB: {
      const data = action.payload as BreadcrumbData;

      let rawTechFilterValues = state.rawTechFilterValues;
      let appliedTechFilterValues = state.appliedTechFilterValues;
      if (data.type === BreadcrumbType.TECH_VALUE) {
        const fullCode = data.code + "." + data.valueType.toLowerCase();
        rawTechFilterValues = { ...rawTechFilterValues };
        delete rawTechFilterValues[fullCode];
        appliedTechFilterValues = { ...rawTechFilterValues };
        delete appliedTechFilterValues[fullCode];
      }

      return {
        ...state,
        currentPage: 0,
        breadcrumbs: state.breadcrumbs.filter((bc) => bc !== data),
        rawTechFilterValues,
        appliedTechFilterValues,
      };
    }

    case CLEAR_ALL: {
      const { searchPageType } = action.payload as { searchPageType: SearchPageType };
      const isCampaignPage = searchPageType === SearchPageType.Campaign;
      return {
        ...state,
        currentPage: 0,
        breadcrumbs: [],
        appliedTechFilterValues: {},
        rawTechFilterValues: {},
        text: isCampaignPage ? "" : state.text,
        salesCampaignFilter: !isCampaignPage ? [] : state.salesCampaignFilter,
      };
    }

    case UPDATE_TECH_VALUE: {
      const { code, type, value } = action.payload as {
        code: string;
        type: string;
        value?: number;
      };

      const fullCode = code + "." + type;
      if (value !== undefined) {
        return {
          ...state,
          rawTechFilterValues: {
            ...state.rawTechFilterValues,
            [fullCode]: value,
          },
        };
      } else {
        const rawTechFilterValues = { ...state.rawTechFilterValues };
        delete rawTechFilterValues[fullCode];
        return {
          ...state,
          rawTechFilterValues,
        };
      }
    }

    case APPLY_ALL: {
      const a = action as ReturnType<typeof applyAllFiltersAction>;
      const unitMode = a.payload.unitMode;
      const differences = diff(state.appliedTechFilterValues, state.rawTechFilterValues);

      let differentKeys: string[] = [];
      if (differences) {
        differentKeys = differences.map((diffItem) => diffItem.path![0] as string);
      }

      // Step 1 - remove all Tech Value breadcrumbs which's value has changed - they need to be at the end of the list
      const breadcrumbs = state.breadcrumbs.filter(
        (bc) =>
          bc.type !== BreadcrumbType.TECH_VALUE ||
          differentKeys.indexOf(`${bc.code}.${bc.valueType.toLowerCase()}`) === -1,
      );

      // Step 2 - for each Tech Value, add it if the code and valueType combination is unique
      // We need to add them in order...
      const config = unitMode === UnitMode.Metric ? state.filterConfig : state.filterConfigImperial;
      if (config) {
        config
          .filter(isTechValueFilterCategory)
          .flatMap((category) => category.items)
          .forEach((filter) => {
            let key = `${filter.uid}.min`;
            let keyChanged = differentKeys.indexOf(key) !== -1;
            const minVal = state.rawTechFilterValues[key];
            if (minVal && keyChanged) {
              breadcrumbs.push({
                type: BreadcrumbType.TECH_VALUE,

                value: minVal,
                valueType: "Min",
                code: filter.uid,
                uom: getUom(filter.title),
              });
            }
            key = `${filter.uid}.max`;
            keyChanged = differentKeys.indexOf(key) !== -1;
            const maxVal = state.rawTechFilterValues[key];
            if (maxVal && keyChanged) {
              breadcrumbs.push({
                type: BreadcrumbType.TECH_VALUE,

                value: maxVal,
                valueType: "Max",
                code: filter.uid,
                uom: getUom(filter.title),
              });
            }
          });
      }

      return {
        ...state,
        appliedTechFilterValues: state.rawTechFilterValues,
        breadcrumbs,
        currentPage: 0,
      };
    }

    case UPDATE_SEARCH_TEXT: {
      const { searchText, searchPageType } = action.payload as { searchText: string; searchPageType: SearchPageType };
      const clearSalesCampaignFilter = searchPageType !== SearchPageType.Campaign;

      return {
        ...state,
        text: searchText,
        sort: sortInitialState,
        currentPage: 0,
        contentPage: 0,
        breadcrumbs: [],
        rawTechFilterValues: {},
        appliedTechFilterValues: {},
        salesCampaignFilter: clearSalesCampaignFilter ? [] : state.salesCampaignFilter,
      };
    }

    case SELECT_PRODUCT_GROUP:
    case SELECT_PRODUCT_GROUP_MULTI: {
      const { code: selectedProductGroup, newConfig } = action.payload as {
        code: string;
        newConfig?: GetFilterConfigApiResult;
      };

      return produce(state, (draft) => {
        if (newConfig) {
          draft.filterConfig = newConfig.filterGroups;
          draft.tabularViewColConfig = newConfig.tabularViewCols;
          draft.filterConfigImperial = newConfig.filterGroupsImperial;
          draft.tabularViewColConfigImperial = newConfig.tabularViewColsImperial;
        }

        let selectedProductGroups;
        if (action.type === SELECT_PRODUCT_GROUP_MULTI) {
          if (state.selectedProductGroups.includes(selectedProductGroup)) {
            selectedProductGroups = state.selectedProductGroups.filter((group) => group !== selectedProductGroup);
          } else if (selectedProductGroup) {
            selectedProductGroups = state.selectedProductGroups.concat(selectedProductGroup);
          } else {
            selectedProductGroups = state.selectedProductGroups;
          }
        } else {
          selectedProductGroups = selectedProductGroup ? [selectedProductGroup] : [];
        }
        draft.selectedProductGroups = selectedProductGroups;
        draft.currentPage = 0;
      });
    }
    case SET_SELECTED_PRODUCT_GROUPS: {
      const { values } = action.payload as {
        values: string[];
      };

      return produce(state, (draft) => {
        draft.selectedProductGroups = values;
        draft.currentPage = 0;
      });
    }

    case UPDATE_ASSISTANT_VALUES: {
      const { code } = action.payload as { code: string };
      let assistantValuesResult: string[] = [];

      if (state.assistantValues.includes(code)) {
        state.assistantValues.forEach((value) => {
          if (value !== code) {
            assistantValuesResult.push(value);
          }
        });
      } else {
        assistantValuesResult = [...state.assistantValues, code];
      }
      return {
        ...state,
        currentPage: 0,
        assistantValues: assistantValuesResult,
      };
    }

    case UPDATE_IS_SELECTION_ASSISTANT: {
      const { value } = action.payload as { value: SelectionAssistantType };

      return {
        ...state,
        isSelectionAssistant: value,
      };
    }

    case RESET_SA_FILTERS: {
      return {
        ...state,
        breadcrumbs: [],
        rawTechFilterValues: {},
        appliedTechFilterValues: {},
        selectedProductGroups: [],
        assistantValues: [],
      };
    }

    case UPDATE_CAMPAIGN_FILTER: {
      const campaignCode = action.payload as string[];
      return {
        ...state,
        salesCampaignFilter: campaignCode,
      };
    }

    case UPDATE_RELEVANT_FILTER: {
      const enabled = action.payload as unknown as boolean;
      return {
        ...state,
        showOnlyRelevant: enabled,
      };
    }

    case SRP_FETCH_RECOMMENDED_PRODUCTS_START: {
      return {
        ...state,
        productsOnSaleInFlight: true,
        productsOnSale: [],
      };
    }

    case SRP_FETCH_RECOMMENDED_PRODUCTS_SUCCESS: {
      const products = action.payload as SrpProductData[];
      return {
        ...state,
        productsOnSale: products.map((product) => product.code),
        productsOnSaleInFlight: false,
      };
    }

    case SRP_FETCH_RECOMMENDED_PRODUCTS_FAILURE: {
      return {
        ...state,
        productsOnSaleInFlight: false,
      };
    }

    default:
      return state;
  }
};

function getUom(title: string) {
  return title.substring(title.lastIndexOf("(") + 1, title.lastIndexOf(")"));
}
